import { useMemo, useState } from 'react'
import { DateTime } from 'luxon'

import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import {
  selectFederalEstimateByTaxQuarter,
  selectStatusForTaxEstimate,
} from '@/features/taxes/quarterly/userTaxEstimates.selector'
import type { UserTaxEstimate } from '@/features/taxes/quarterly/userTaxEstimates.slice'
import { EstimateStatus } from '../../service'
import { ZeroEstimateTooltip } from './EstimateBreakdownV3Tooltips'
import { centsToEstimateAmount } from './utils'
import { reconcileEstimateDisplay } from './estimateReconciliation'
import { useBreakdownTransparency } from './useBreakdownTransparency'
import useTaxPaymentInfoFlyout from '../TaxPaymentInfoFlyout'

import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { UserTaxCalculation } from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import { UpdateQuarterlyTaxEstimateDialog } from '../UpdateQuarterlyTaxEstimateDialog'
import { TextColors, TypographyStyles } from '@/styles'
import { Button, Card, Label, Link, Text, CardColors, Popover } from '@/ui'

const HowMuchShouldIPayRow = () => (
  <Link
    href="https://support.joinheard.com/hc/en-us/articles/22050337435415-How-to-manually-calculate-quarterly-estimate-tax"
    newPage
    className="w-full"
  >
    How much should I pay?
  </Link>
)

export const PaymentCard = ({
  taxCalc,
  estimateDetails,
  isSample,
  estimateOverride,
  calcAmountInCents,
}: {
  taxCalc: UserTaxCalculation | undefined
  estimateDetails: QuarterlyTaxEstimateDetail | undefined
  isSample?: boolean
  /** Renders a state estimate instead of the quarter's federal one. */
  estimateOverride?: UserTaxEstimate
  /** The calculated amount to reconcile against, when it is not the federal one. */
  calcAmountInCents?: number | null
}) => {
  const [estimateModalOpen, setEstimateModalOpen] = useState(false)
  const quarter = estimateDetails?.taxQuarter

  const federalEstimate = useReselector(
    selectFederalEstimateByTaxQuarter,
    `${estimateDetails?.taxYear}-${estimateDetails?.taxQuarter}`
  )
  const taxEstimate = estimateOverride ?? federalEstimate

  const openFlyout = useTaxPaymentInfoFlyout({
    estimateId: taxEstimate?.id,
    openQTEModal: () => setEstimateModalOpen(true),
  })

  const estimateStatus = useReselector(
    selectStatusForTaxEstimate,
    taxEstimate?.id
  )

  const transparencyEnabled = useBreakdownTransparency()

  const taxAuthority =
    taxEstimate?.type === 'state' && taxEstimate.filingState
      ? taxEstimate.filingState
      : 'federal'

  const reconciliation = reconcileEstimateDisplay({
    calcAmountInCents:
      calcAmountInCents ?? taxCalc?.federalQuarterlyEstimateInCents,
    releasedAmountInCents:
      transparencyEnabled && !isSample
        ? taxEstimate?.estimateInCents
        : undefined,
  })

  const shownStatus = isSample ? EstimateStatus.readyForPayment : estimateStatus

  const conditionalContent = useMemo(() => {
    switch (shownStatus) {
      // Payment card shouldn't display for these states
      case EstimateStatus.joinedTooLate:
      case EstimateStatus.upcoming:
      case EstimateStatus.awaitingEstimate:
        return null
      case EstimateStatus.unpaidWithResponse:
        return {
          label: <Label color="gray">Unpaid</Label>,
          timeText: 'Estimated Payment',
          ctaButtons: (
            <>
              <Text className="mb-4">
                You’ve indicated that you didn’t pay {taxAuthority} taxes this
                quarter.
              </Text>

              <HowMuchShouldIPayRow />

              <Button
                className="w-full mt-4"
                onClick={() => setEstimateModalOpen(true)}
                variant="secondary"
              >
                Update Payment
              </Button>
            </>
          ),
        }
      case EstimateStatus.paid: {
        // There is a sort of sub status here however this is the only component that seems to treat it differently
        if (!taxEstimate?.userDocumentId) {
          return {
            label: <Label color="orange">Action Needed</Label>,
            timeText: `Estimated Payment paid ${taxEstimate?.paidAt && DateTime.fromISO(taxEstimate.paidAt).toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}`,
            ctaButtons: (
              <>
                <Text color={TextColors.MUTED} className="w-full">
                  You’re missing a receipt for your payment.
                </Text>
                <Button
                  className="w-full"
                  onClick={() => setEstimateModalOpen(true)}
                  variant="secondary"
                >
                  Update Receipt
                </Button>
              </>
            ),
          }
        }

        return {
          label: <Label color="green">Paid</Label>,
          timeText: `Estimated Payment paid ${taxEstimate.paidAt && DateTime.fromISO(taxEstimate.paidAt).toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}`,
          ctaButtons: (
            <Button
              className="w-full"
              onClick={() => setEstimateModalOpen(true)}
              variant="secondary"
            >
              Update Payment
            </Button>
          ),
        }
      }
      case EstimateStatus.noIncomeTax:
      case EstimateStatus.estimateIsZero:
        return {
          label: <Label color="gray">No Action Needed</Label>,
          timeText: 'Estimated Payment',
          ctaButtons: (
            <>
              <Text color={TextColors.MUTED} className="mb-2">
                You’re set! No action is needed.
              </Text>

              <Popover>
                <Popover.Trigger asChild>
                  <Button variant="link">Why don’t I owe anything?</Button>
                </Popover.Trigger>
                <Popover.Content className="max-w-md">
                  <ZeroEstimateTooltip />
                </Popover.Content>
              </Popover>

              <Button
                className="w-full"
                onClick={() => setEstimateModalOpen(true)}
                variant="secondary"
              >
                Update Payment
              </Button>
            </>
          ),
        }
      case EstimateStatus.unpaidWithoutResponse:
        return {
          label: <Label color="orange">Action Needed</Label>,
          timeText: 'Estimated Payment',
          ctaButtons: (
            <>
              <Text className="mb-4">
                Please update your payment, even if you didn&apos;t make any
                payments in Q{quarter}.
                <br />
                <br />
                We need this to provide accurate estimates for next quarter.
              </Text>

              <HowMuchShouldIPayRow />

              <Button
                className="w-full mt-4"
                onClick={() => setEstimateModalOpen(true)}
                variant="secondary"
              >
                Update Payment
              </Button>
            </>
          ),
        }

      case EstimateStatus.readyForPayment:
        return {
          label: <Label color="orange">Ready for Payment</Label>,
          timeText: `Estimated Payment due ${estimateDetails?.irsPaymentDueAt && isoToUTCDateTime(estimateDetails.irsPaymentDueAt).toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}`,
          ctaButtons: (
            <div className="flex flex-col">
              <Button
                className="w-full"
                onClick={openFlyout}
                disabled={isSample}
              >
                View & Pay
              </Button>
              <br />
              <Button
                className="w-full"
                variant="secondary"
                onClick={() => setEstimateModalOpen(true)}
                disabled={isSample}
              >
                Mark as Paid
              </Button>
            </div>
          ),
        }
      default:
        return shownStatus satisfies never
    }
  }, [
    isSample,
    estimateDetails?.irsPaymentDueAt,
    taxEstimate?.paidAt,
    taxEstimate?.userDocumentId,
    openFlyout,
    quarter,
    shownStatus,
    taxAuthority,
  ])

  if (
    !estimateDetails ||
    (!taxEstimate && !isSample) ||
    (!taxCalc && !isSample) ||
    conditionalContent === null
  ) {
    return null
  }

  return (
    <>
      <Card color={CardColors.LEVEL_1}>
        <div className="w-full flex flex-col gap-4 sm:gap-6">
          <div>{conditionalContent.label}</div>
          <Text variant={TypographyStyles.H3}>
            {centsToEstimateAmount(reconciliation.displayInCents)}
          </Text>

          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {conditionalContent.timeText}
          </Text>

          {conditionalContent.ctaButtons}
        </div>

        {taxEstimate && (
          <UpdateQuarterlyTaxEstimateDialog
            open={estimateModalOpen}
            close={() => setEstimateModalOpen(false)}
            estimate={taxEstimate}
          />
        )}
      </Card>

      <Text color={TextColors.MUTED} className="mt-6 sm:mt-8 w-full">
        Updated on{' '}
        {taxCalc?.calculatedAt &&
          DateTime.fromISO(taxCalc.calculatedAt).toFormat(
            DATE_FORMATS_LUXON.DISPLAY_TIMEZONE
          )}
      </Text>
    </>
  )
}
